<template>
  <div>
    <danger-alert v-if="isUploaded" class="mx-5" message="振込明細はすでにアップロード済みです。" />
    <send-form :can-back="canBack()" @back="toViewer" @submit.prevent="doSubmit">
      <span class="h4 border-bottom border-2 mb-3">依頼詳細</span>
      <text-browse-item label="依頼ID" :value="id" />
      <text-browse-item label="ステータス" :value="status_text" class="mb-5" />
      <datetime-browse-item label="依頼日時" :value="request_at" />
      <text-browse-item label="依頼物件名・棟名" :value="building.name" />
      <text-browse-item label="依頼物件名・棟名（カナ）" :value="building.name_kana" />
      <text-browse-item label="依頼物件住戸番号" :value="building_house_number" />
      <text-browse-item label="所有者" :value="owner_name" />
      <text-browse-item label="依頼物件郵便番号" :value="building.postal_code" />
      <text-browse-item label="依頼物件住所" :value="building.address" />
      <div v-show="requiredDocumentType !== $constant.DOCUMENT_TYPE.HIDDEN">
        <file-browse-item label="依頼主確認書類" :value="required_document_path" :file-name="requiredDocumentTypeName" :is-enable="!!required_document_path" />
      </div>
      <text-browse-item label="発行書類" :value="issueDocumentName" />
      <text-browse-item label="手数料の支払方法" :value="paymentMethodName" />
      <text-browse-with-unit-item label="請求金額" :value="billing_price" unit="円" />
      <text-browse-with-unit-item label="発行所要日数" :value="days_to_issue" unit="営業日" />
      <textarea-browse-item v-if="canShowBankInfo" label="銀行情報" :value="accountBank" />
      <textarea-browse-item v-if="canShowBankInfo" label="口座情報" :value="accountInfo" />
      <textarea-browse-item v-if="canShowBankInfo" label="口座名義" :value="accountFolder" />
      <textarea-browse-item label="備考" :value="note" class="mb-5" />
      <text-browse-item v-if="!isOwner" label="依頼仲介業者" :value="requester.brokerage_company" />
      <text-browse-item label="依頼者" :value="requester.requested_by" />
      <text-browse-item label="依頼者住所" :value="requester.address" />
      <textarea-browse-item label="連絡先" :value="contact" />
      <licence-browse-item v-if="!isOwner" label="免許番号" :type="requester.licence_type" :number="requester.licence_number" :update="requester.licence_update" />
      <vuex-multi-file-upload-form-item :issues="transfer_statement_files"
                                        @delete="confirmDelete"
                                        :callback="callbackUploadFiles"
                                        :upload="doNewTransferStatementMultiUpload"
                                        :error-message="uploadFileErrorMessage"
                                        :reset-errors="resetErrors"
      />
      <vuex-date-form-item label="振込日" v-model="transfer_date" :required="true" bind="transfer_date" @input="doInputTransferDate" />
      <p class="date_error">{{ errortext }}</p>
      <danger-alert v-if="alertMessage" :message="alertMessage" />
      <template #expansion-buttons-right>
        <change-status-button v-if="canCancel" text="キャンセル" class="btn-danger px-4"
                              @click="confirmOperateWithReason($constant.ISSUANCE_REQUEST_OPERATION.CANCELED)"
        />
      </template>
    </send-form>
    <teleport to="body">
      <delete-confirm-modal @delete="doTransferStatementDelete" ref="deleteConfirmModal" />
      <send-confirm-modal v-show="callbackUploadFiles" @send="doSubmit" ref="sendConfirmModal"  v-model="transfer_date" @inputValue="doInputTransferDate"/>
      <edit-confirm-modal-with-input-area first-description-text="理由を入力して更新ボタンを押下してください。"
                                          second-description-text="入力した内容は管理会社に通知されます。"
                                          input-text-label="理由"
                                          :text-max-length="5000"
                                          :required="true"
                                          @beforeUpdate="setNotificationReason"
                                          @update="doOperation()"
                                          ref="confirmModalWithReason"
                                          default-value="不要になったため。"
      />
    </teleport>
  </div>
</template>

<script>
import {mapState, mapGetters, mapMutations} from "vuex";
import {convertPaymentMethodToName} from '@/constants/payment_method';
import AddonLoadPageEvent from '@/mixin/AddonLoadPageEvent';
import DangerAlert from "@/views/atoms/alerts/DangerAlert";
import SendForm from "@/views/templates/forms/SendForm";
import TextBrowseItem from "@/views/molecules/browseItems/TextBrowseItem";
import DatetimeBrowseItem from "@/views/molecules/browseItems/DatetimeBrowseItem";
import TextBrowseWithUnitItem from '@/views/molecules/browseItems/TextBrowseWithUnitItem';
import FileBrowseItem from "@/views/molecules/browseItems/FileBrowseItem";
import LicenceBrowseItem from "@/views/molecules/browseItems/LicenceBrowseItem";
import TextareaBrowseItem from "@/views/molecules/browseItems/TextareaBrowseItem";
import VuexMultiFileUploadFormItem from "@/views/molecules/formItems/VuexMultiFileUploadFormItem";
import DeleteConfirmModal from "@/views/molecules/modals/DeleteConfirmModal";
import SendConfirmModal from '@/views/molecules/modals/SendConfirmModal';
import ChangeStatusButton from '@/views/molecules/buttons/ChangeStatusButton';
import EditConfirmModalWithInputArea from "@/views/molecules/modals/EditConfirmModalWithInputArea";
import VuexDateFormItem from "@/views/molecules/formItems/VuexDateFormItem";
import moment from 'moment';
import bankAccountTypeOptions from '@/constants/bank_account_type_options';
export default {
  name: 'IssuanceRequestUploadTransferStatement',
  components: {
    DangerAlert,
    SendForm,
    TextBrowseItem,
    DatetimeBrowseItem,
    TextBrowseWithUnitItem,
    FileBrowseItem,
    LicenceBrowseItem,
    TextareaBrowseItem,
    DeleteConfirmModal,
    SendConfirmModal,
    VuexMultiFileUploadFormItem,
    ChangeStatusButton,
    EditConfirmModalWithInputArea,
    VuexDateFormItem,
  },
  mixins: [AddonLoadPageEvent],
  provide: {
    bindModule: 'issuance_request_form'
  },
  inject: [
    '$constant'
  ],
  data() {
    return {
      delete_payload: null,
      operation: null,
      alertMessage: null,
      transfer_date: moment().format('YYYY/MM/DD'),
      errortext:'',
    }
  },
  computed: {
    ...mapState('issuance_request_form', {
      id: state => state.data.id ? state.data.id.toString() : null,
      status: state => state.data.status,
      status_text: state => state.data.status_text,
      request_at: state => state.data.request_at,
      required_document_path: state => state.data.required_document_path,
      transfer_statement_path: state => state.data.transfer_statement_path,
      building: state => state.data.building,
      building_house_number: state => state.data.building_house_number,
      owner_name: state => state.data.owner_name,
      billing_price: state => state.data.billing_price,
      requester: state => state.data.requester,
      note: state => state.data.note,
      contact: state => state.data.contact,
      payment_method_code: state => state.data.payment_method,
      transfer_statement_files: state => state.data.transfer_statement_files,
      transfer_notification_date: state => state.data.transfer_notification_date,
      errors: state => state.errors,
      errors_multi: state => state.errorsMulti,
      requiredDocumentType: state => state.data.required_document_type,
      days_to_issue: state => state.data.building.management_company.days_to_issue,
      transfer_information: state => state.data.transfer_information,
      management_association_bank: state => state.data.management_association_bank,
      bank_multiple: state => state.data.building.management_company.bank_multiple,
      main_bank: state => state.data.building.management_company.main_bank,
    }),
    ...mapState('account', {
      is_guest: state => state.is_guest,
      guest_issuance_request_id: state => state.guest.issuance_request_id,
      guestType: state => state.guest_type,
    }),
    ...mapGetters('issuance_request_form', [
      'requiredDocumentTypeName',
      'issueDocumentName',
    ]),
    isUploaded() {
      if (this.status === this.$constant.ISSUANCE_REQUEST_STATUS.TRANSFER_STATEMENT_NG.code) {
        return false;
      }
      return !!this.transfer_notification_date;
    },
    canDelete: function () {
      return this.$constant.ALLOWED_DELETE_TRANSFER_STATEMENT_CODE.indexOf(this.status) >= 0
    },
    canUpload: function () {
      return this.$constant.ALLOWED_UPLOAD_TRANSFER_STATEMENT_CODE.indexOf(this.status) >= 0
    },
    paymentMethodName() {
      return convertPaymentMethodToName(this.payment_method_code);
    },
    isOwner() {
      return this.guestType === this.$constant.GUEST_TYPE.OWNER.code;
    },
    uploadFileErrorMessage() {
      const messages = [];
      const seen = new Set();
      if (this.errors.errorLimitFile) {
        const msg = this.errors.errorLimitFile;
        const key = `:${msg}`;
        if (!seen.has(key)) {
          messages.push({ transfer_statement_file: [msg], name: '' });
          seen.add(key);
        }
      }
      if (Array.isArray(this.errors_multi)) {
        this.errors_multi.forEach((e) => {
          const raw = e?.transfer_statement_files || e?.transfer_statement_file;
          const name = e?.name || '';
          if (!raw) return;
          const msgArray = Array.isArray(raw) ? raw : [raw];
          msgArray.forEach((msg) => {
            const key = `${name}:${msg}`;
            if (!seen.has(key)) {
              messages.push({ transfer_statement_file: [msg], name });
              seen.add(key);
            }
          });
        });
      }
      return messages;
    },

    canCancel() {
      return this.status == this.$constant.ISSUANCE_REQUEST_STATUS.WAITING_BANK_TRANSFER.code
        && this.transfer_statement_files.length === 0;
    },
    transferInfoFromBankObject() {
      const bank = this.bank_multiple
        ? (this.management_association_bank || this.main_bank || null)
        : this.main_bank;
      if (!bank) return null;

      const typeText = bankAccountTypeOptions.list
        .find(t => t.value === bank.bank_account_type)?.text ?? '';

      return [
        bank.bank_name ?? '',
        bank.bank_branch_name ?? '',
        typeText,
        bank.bank_account_no ?? '',
        bank.bank_account_name ?? '',
        bank.bank_account_name_kana ?? '',
      ];
    },
    displayTransferInformation() {
      if (Array.isArray(this.transfer_information) && this.transfer_information.length) {
        return this.transfer_information;
      }

      const fallback = this.transferInfoFromBankObject;
      return fallback?.length ? fallback : [];
    },
    canShowBankInfo() {
      return this.billing_price !== 0
        && this.payment_method_code === this.$constant.PAYMENT_METHOD_CODE.PREPAID.code
        && this.displayTransferInformation.length > 0;
    },
    accountBank() {
      return this.displayTransferInformation
        ? this.displayTransferInformation.slice(0, 2).join('　')
        : '';
    },
    accountInfo() {
      return this.displayTransferInformation
        ? this.displayTransferInformation.slice(2, 4).join('　')
        : '';
    },
    accountFolder() {
      return this.displayTransferInformation
        ? this.displayTransferInformation.slice(4, 6).join('　')
        : '';
    },
  },
  methods: {
    ...mapMutations('issuance_request_form', [
      'setUploadTransferStatement',
      'resetErrors',
    ]),
    async loadPage() {
      if (this.is_guest){
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
        return this.$store.dispatch('issuance_request_form/load', {
          // ゲストの場合は認証時に発行依頼IDを取得済み
          id: this.is_guest ? this.guest_issuance_request_id : this.$route.params.id
        }).catch(() => this.$root.failApi());
    },
    async doSubmit() {
      if (!this.transfer_date){
        this.errortext='振込日を入力してください';
        return;
      }
      this.resetErrors();
      this.errortext = '';
      this.$store.commit('issuance_request_form/setTransferDate', this.transfer_date);
      if (this.is_guest){
        await this.$store.dispatch('account/authGuest', {
        access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      this.$store.dispatch('issuance_request_form/transferComplete').then(() => {
        this.$router.push({name: this.is_guest ? 'GuestIssuanceRequestComplete' : 'IssuanceRequestComplete'});
      }).catch(reason => this.$root.failApi(reason));
    },
    confirmDelete(payload) {
      this.delete_payload = payload;
      this.$refs.deleteConfirmModal.show();
    },
    async doTransferStatementDelete() {
      if (this.is_guest){
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      this.$store.dispatch('issuance_request_form/deleteTransferStatement', {id: this.delete_payload.id}).then(() => {
        return this.loadPage();
      }).then(() => {
        this.$root.showFlashMessage();
      }).catch(reason => this.$root.failApi(reason));
    },
    async doNewTransferStatementUpload() {
      if (this.is_guest){
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      this.$store.dispatch('issuance_request_form/uploadTransferStatement').then(() => {
        return this.loadPage();
      }).then(() => {
        if (!this.transfer_date){
          this.loadPage();
        } else {
          this.$root.showFlashMessage();
        }
        this.$refs.sendConfirmModal.show();
      }).catch(reason => this.$root.failApi(reason));
    },
    async callbackUploadFiles(isShowModal) {
      isShowModal && this.$refs.sendConfirmModal.show();
      this.loadPage().then(() => {
        this.$root.showFlashMessage();
      });
    },
   
    async doInputTransferDate() {
      try {
        await this.$store.dispatch(
          'issuance_request_form/loadTransferDate'
        );
      } catch (reason) {
        this.$root.failApi(reason);
      }
      this.loadPage().then(() => {
        this.$root.showFlashMessage();
      });
    },
    async doNewTransferStatementMultiUpload(payload) {
      return await this.$store.dispatch('issuance_request_form/multiUploadTransferStatement', payload).catch(reason => this.$root.failApi(reason));
    },
    toViewer() {
      this.$router.go(-1);
    },
    canBack() {
      // 画面遷移して起動した場合true
      if (this.$route.params.showBackButton) {
        return JSON.parse(this.$route.params.showBackButton);
      }
      return false
    },
    setNotificationReason(value) {
      this.notificationReason = value;
    },
    confirmOperateWithReason(operation) {
      this.operation = operation;
      this.$refs.confirmModalWithReason.show();
    },
    async doOperation() {
      if (this.is_guest){
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      this.$store.dispatch('issuance_request_form/operate', {
        operation: this.operation,
        notificationReason: this.notificationReason
      }).then(() => {
        if (this.canBack()) {
          // 画面遷移して起動した場合、ひとつ前の画面にリダイレクト
          this.toViewer();
        } else {
          this.$router.push(this.is_guest ?
            {name: 'GuestIssuanceRequestViewer', params: {'id': this.guest_issuance_request_id}} :
            {name: 'IssuanceRequestViewer', params: {'id': this.$route.params.id}}
          );
        }
        this.$root.showFlashMessage();
      }).catch((message) => {
        if (message) {
          this.alertMessage = message;
        } else {
          this.$root.failApi();
        }
      });
    },
  }
}
</script>
<style>
.date_error{
  color: red;
}
</style>
